/**
 * The graph widget's data, independent of the renderer (spec §9.7 `graph`): which nodes and edges a
 * workspace yields, what changed between two versions, and where new nodes go so that nothing already
 * on screen moves (§9.19 no surprises).
 */
import type { LoroDoc } from "loro-crdt";
import type { FilterAst } from "../../core/filter";
import { visibleTasks } from "../shared";

export type EdgeKind = "child" | "depends" | "relates" | "seq" | "group";

export interface GraphNode {
  id: string;
  label: string;
  done: boolean;
  kind: "task" | "group";
}

export interface GraphEdge {
  id: string;
  source: string;
  target: string;
  kind: EdgeKind;
}

export interface Elements {
  nodes: GraphNode[];
  edges: GraphEdge[];
}

export interface Point {
  x: number;
  y: number;
}

export const DEFAULT_EDGES: EdgeKind[] = ["child", "depends", "relates", "seq"];

export function buildElements(
  doc: LoroDoc,
  filter: FilterAst | undefined,
  kinds: EdgeKind[],
  nowMs: number,
): Elements {
  const v = visibleTasks(doc, filter, nowMs, { includeDone: true, includeNotes: true, includeSomeday: true });
  const ids = new Set(v.tasks.map((x) => x.id));
  const nodes: GraphNode[] = v.tasks.map((x) => ({
    id: x.id,
    label: x.title.slice(0, 40),
    done: Boolean(x.doneAt),
    kind: "task",
  }));
  const edges: GraphEdge[] = [];
  const s = v.snapshot;
  if (kinds.includes("child"))
    for (const x of v.tasks) {
      const p = s.parentOf(x.id);
      if (p && ids.has(p)) edges.push({ id: `c-${x.id}`, source: p, target: x.id, kind: "child" });
    }
  for (const [eid, e] of Object.entries(s.edges))
    if (kinds.includes(e.kind as EdgeKind) && ids.has(e.from) && ids.has(e.to))
      edges.push({ id: eid, source: e.from, target: e.to, kind: e.kind as EdgeKind });
  if (kinds.includes("seq"))
    for (const [sid, q] of Object.entries(s.sequences)) {
      const items = (q.items ?? []).filter((i) => ids.has(i));
      for (let i = 1; i < items.length; i++)
        edges.push({ id: `s-${sid}-${i}`, source: items[i - 1], target: items[i], kind: "seq" });
    }
  if (kinds.includes("group"))
    for (const [gid, g] of Object.entries(s.groups)) {
      const members = Object.keys(g.members ?? {}).filter((m) => g.members?.[m] && ids.has(m));
      if (!members.length) continue;
      nodes.push({ id: `g:${gid}`, label: g.title ?? gid, done: false, kind: "group" });
      for (const m of members)
        edges.push({ id: `g-${gid}-${m}`, source: `g:${gid}`, target: m, kind: "group" });
    }
  return { nodes, edges };
}

export interface Diff {
  addNodes: GraphNode[];
  removeNodes: string[];
  changeNodes: GraphNode[];
  addEdges: GraphEdge[];
  removeEdges: string[];
  changeEdges: GraphEdge[];
}

const sameNode = (a: GraphNode, b: GraphNode) =>
  a.label === b.label && a.done === b.done && a.kind === b.kind;
const sameEdge = (a: GraphEdge, b: GraphEdge) =>
  a.source === b.source && a.target === b.target && a.kind === b.kind;

/** What to add, remove and update to get from `prev` to `next`, by id. */
export function diffElements(prev: Elements, next: Elements): Diff {
  const pn = new Map(prev.nodes.map((n) => [n.id, n]));
  const nn = new Map(next.nodes.map((n) => [n.id, n]));
  const pe = new Map(prev.edges.map((e) => [e.id, e]));
  const ne = new Map(next.edges.map((e) => [e.id, e]));
  const d: Diff = {
    addNodes: [],
    removeNodes: [],
    changeNodes: [],
    addEdges: [],
    removeEdges: [],
    changeEdges: [],
  };
  for (const n of next.nodes) {
    const old = pn.get(n.id);
    if (!old) d.addNodes.push(n);
    else if (!sameNode(old, n)) d.changeNodes.push(n);
  }
  for (const id of pn.keys()) if (!nn.has(id)) d.removeNodes.push(id);
  for (const e of next.edges) {
    const old = pe.get(e.id);
    if (!old) d.addEdges.push(e);
    else if (!sameEdge(old, e)) d.changeEdges.push(e);
  }
  for (const id of pe.keys()) if (!ne.has(id)) d.removeEdges.push(id);
  return d;
}

export const isEmptyDiff = (d: Diff) =>
  !d.addNodes.length &&
  !d.removeNodes.length &&
  !d.changeNodes.length &&
  !d.addEdges.length &&
  !d.removeEdges.length &&
  !d.changeEdges.length;

/** spacing of the placement, in model units (both renderers draw nodes ~14 px wide at zoom 1) */
export const RANK_GAP = 160;
export const ROW_GAP = 48;

/**
 * Positions for nodes that have none, leaving every placed node where it is. A node with placed
 * predecessors goes one rank to their right (the hierarchical layout reads left to right); one with
 * only placed successors one rank to their left; one with neither below everything. A spot already
 * taken moves it down a row until it is free.
 */
export function placeNew(ids: string[], edges: GraphEdge[], placed: Map<string, Point>): Map<string, Point> {
  const out = new Map<string, Point>();
  const all = new Map(placed);
  const free = (p: Point) =>
    ![...all.values()].some((q) => Math.abs(q.x - p.x) < RANK_GAP / 2 && Math.abs(q.y - p.y) < ROW_GAP * 0.8);
  const settle = (p: Point): Point => {
    const q = { ...p };
    for (let i = 0; i < 500 && !free(q); i++) q.y += ROW_GAP;
    return q;
  };
  let pending = ids.filter((id) => !all.has(id));
  // nodes connected to placed ones first, so chains of new nodes line up one after the other
  for (let round = 0; pending.length && round < ids.length + 1; round++) {
    const next: string[] = [];
    for (const id of pending) {
      const preds = edges.filter((e) => e.target === id && all.has(e.source)).map((e) => all.get(e.source)!);
      const succs = edges.filter((e) => e.source === id && all.has(e.target)).map((e) => all.get(e.target)!);
      const avgY = (ps: Point[]) => ps.reduce((s, p) => s + p.y, 0) / ps.length;
      let p: Point | null = null;
      if (preds.length) p = { x: Math.max(...preds.map((q) => q.x)) + RANK_GAP, y: avgY(preds) };
      else if (succs.length) p = { x: Math.min(...succs.map((q) => q.x)) - RANK_GAP, y: avgY(succs) };
      if (!p) {
        next.push(id);
        continue;
      }
      const q = settle(p);
      all.set(id, q);
      out.set(id, q);
    }
    if (next.length === pending.length) {
      // nothing left that touches the drawing: start a new row below it
      const ys = [...all.values()].map((q) => q.y);
      const xs = [...all.values()].map((q) => q.x);
      const base = { x: xs.length ? Math.min(...xs) : 0, y: ys.length ? Math.max(...ys) + ROW_GAP * 1.5 : 0 };
      const q = settle(base);
      all.set(next[0], q);
      out.set(next[0], q);
      next.shift();
    }
    pending = next;
  }
  return out;
}
